import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowRight, HiExternalLink } from "react-icons/hi";
import data from "../../data/index.json";
import { Section, SectionHeader, reveal } from "../../components";

const ventures = (data.portfolio || [])
  .filter((p) => p.venture)
  .sort((a, b) => (a.ventureOrder || 0) - (b.ventureOrder || 0));

/**
 * The two flagship platforms carry the company's argument, so they get full
 * editorial spreads that alternate direction rather than a pair of cards.
 */
const AIVentures = () => {
  const { t } = useTranslation();

  return (
    <Section id="ventures" tone="deep">
      <SectionHeader
        index="03"
        label={t("Our AI Ventures")}
        title={
          <>
            {t("Two platforms,")} <em>{t("in production")}</em>
          </>
        }
        lede={t(
          "Two flagship platforms solving real problems for farmers and entrepreneurs across Burundi and Africa."
        )}
        aside={
          <p className="label max-w-[22ch] leading-relaxed text-white/60">
            <span className="text-gold">★</span>{" "}
            {t("IEEE Top 5 Innovations in Burundi")}
          </p>
        }
      />

      <div className="mt-16 md:mt-24 space-y-20 md:space-y-28">
        {ventures.map((venture, index) => {
          const tech = venture.details?.tech || [];
          const hasLink = venture.link && venture.link.trim() !== "";
          const flip = index % 2 === 1;

          return (
            <motion.article
              key={venture.id}
              {...reveal()}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center border-t border-[color:var(--rule-invert)] pt-12"
            >
              {/* Plate */}
              <div
                className={`lg:col-span-6 ${
                  flip ? "lg:order-2" : ""
                } overflow-hidden bg-paper-deep-2 aspect-[4/3]`}
              >
                <img
                  src={venture.src.replace("./", "/")}
                  alt={t(venture.title)}
                  loading="lazy"
                  className="img-editorial"
                />
              </div>

              {/* Argument */}
              <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                <span className="label text-gold">
                  {String(index + 1).padStart(2, "0")} — {t(venture.tagline)}
                </span>

                <h3 className="display mt-4">{t(venture.title)}</h3>

                <p className="mt-5 text-[0.9375rem] leading-relaxed text-white/65 max-w-measure">
                  {t(venture.description)}
                </p>

                {venture.ventureStats?.length > 0 && (
                  <dl className="grid grid-cols-2 sm:grid-cols-4 mt-8 border-t border-[color:var(--rule-invert)]">
                    {venture.ventureStats.map((stat, i) => (
                      <div
                        key={stat.label}
                        className={`py-5 ${
                          i % 2 === 1 ? "pl-5 border-l border-[color:var(--rule-invert)]" : ""
                        } sm:pl-5 sm:border-l sm:first:border-l-0 sm:first:pl-0 border-[color:var(--rule-invert)]`}
                      >
                        <dd className="display-3 tabular text-[color:var(--ink-invert)]">
                          {stat.value}
                        </dd>
                        <dt className="label mt-2 text-white/45">{t(stat.label)}</dt>
                      </div>
                    ))}
                  </dl>
                )}

                {venture.ventureFeatures?.length > 0 && (
                  <ul className="mt-8 space-y-2.5">
                    {venture.ventureFeatures.map((feature) => (
                      <li
                        key={feature}
                        className="flex gap-3 text-sm text-white/65 leading-relaxed"
                      >
                        <span aria-hidden className="text-gold mt-[0.45em] w-3 h-px bg-gold shrink-0" />
                        <span>{t(feature)}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {tech.length > 0 && (
                  <p className="mt-8 text-xs text-white/40 leading-relaxed">
                    {tech.join("  ·  ")}
                  </p>
                )}

                <div className="flex flex-wrap gap-3 mt-8">
                  {hasLink && (
                    <a
                      href={venture.link}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-gold"
                    >
                      {t("Visit")} {t(venture.title)}
                      <HiExternalLink aria-hidden />
                    </a>
                  )}
                  <Link to={`/project/${venture.id}`} className="btn btn-outline">
                    {t("Case Study")}
                    <HiArrowRight aria-hidden />
                  </Link>
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>
    </Section>
  );
};

export default AIVentures;
